<template lang="pug">
    Tabs.registration-template-list
        el-card.contains-table( shadow="always" )
            .error(v-if="!hasRegistrationTemplates")
                Error(:message="null" :code="403")
            .error(v-else-if="error")
                template(v-if="error.code")
                    Error(:message="$t(error.message || 'Something went wrong!')" :code="error.code")
                template(v-else)
                    Error(:message="$t('Something went wrong!')" :code="501")
            .registration-templates-table(v-else)
                paginated-items(
                    scope="ex"
                    type="evnt_registration_template"
                    :loading="loading"
                    :items="registrationTemplatesPaginated.items"
                    :filters="filters"
                    :show-filters="showFilters"
                    :variables="registrationTemplatesPaginated.variables"
                    :all-columns="registrationTemplatesPaginated.allColumns"
                    :active-columns="registrationTemplatesPaginated.activeColumns"
                    :page-info="registrationTemplatesPaginated.pageInfo"
                    :sort="sort"
                    @pagination-size-change="handleSizeChange"
                    @pagination-current-change="handleCurrentChange"
                    @show-table-columns="handleColsShow"
                    @change-filter="handleFilterInputChange"
                    @change-sort="handleSortInputChange"
                    @click="handleClick"
                )
                    div(slot="top")
                        el-row.table-top(v-loading="loading" type="flex" justify="space-between")
                            .left
                                el-button(type="primary" size="mini" icon="el-icon-fa-plus fas" @click="addRegistrationTemplate")
                                    | {{ $t('New registration form') }}
                            .right.hide-on-empty
                                el-button(
                                    plain
                                    size="mini"
                                    type="primary"
                                    icon="el-icon-fa-filter fas"
                                    @click="toggleFilterVisiblity"
                                    :disabled="disable.showFilters || hasFilters"
                                )
                                    span(v-if="showFilters") {{ $t('Hide filters') }}
                                    span(v-else) {{ $t('Show filters') }}
</template>

<i18n>
{
  "de": {
    "New registration form": "Neues Anmeldeformular",
    "Show filters": "Filter anzeigen",
    "Hide filters": "Filter verbergen",
    "Registration form list could not be fetched.": "Liste der Anmeldeformulare konnte nicht abgerufen werden."
  }
}
</i18n>

<script>
import _ from 'lodash'
import { adminClient as apolloClient } from '@/store/apollo'
import { useSetupStore, useEventManagerRegistrationTemplateStore } from '@/stores'

import router from '@/router'
import pinia from '@/pinia';

import PaginatedItems from '@/components/PaginatedItems'
import Tabs from '@/extensions/eventManager/views/Partials/Tabs'
import Error from '@/components/Error';

export default {
    name: 'RegistrationTemplates',
    data() {
        return {
            error: false,
            disable: {
                showFilters: false,
            }
        }
    },
    components: {
        PaginatedItems,
        Error,
        Tabs
    },
    watch: {
        activeCms(cms) {
            const reload = _.get(cms, 'reloadComponents', true)
            if (reload) this.initialize()
        },
    },
    computed: {
        showFilters() {
            return this.hasFilters || _.get(this.registrationTemplatesPaginated, 'showFilters') || false
        },
        hasFilters() {
            return ! _.isEmpty(this.filters)
        },
        filters() {
            let filters = _.cloneDeep(this.registrationTemplatesPaginated.filters)
            delete filters.cms
            return filters
        },
        setupStore() {
            return useSetupStore(pinia);
        },
        // registration forms are a storefront feature (citystore-ssr)
        hasRegistrationTemplates() {
            return useSetupStore().enabledFeatures.includes('event-manager:registration-templates');
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        registrationTemplateStore() {
            return useEventManagerRegistrationTemplateStore();
        },
        loading() {
            return this.registrationTemplateStore.loading;
        },
        registrationTemplatesPaginated() {
            return this.registrationTemplateStore.registrationTemplatesPaginated;
        },
        sort() {
            const variables = this.registrationTemplatesPaginated.variables;
            const defaultSort = this.registrationTemplatesPaginated.defaultSort || { field: 'updated_at', direction: 'desc' };
            const order = (variables.order !== undefined && variables.order !== '') ? variables.order : defaultSort.field;
            const direction = (variables.direction !== undefined && variables.direction !== '') ? variables.direction : defaultSort.direction;
            return { field: order, direction };
        }
    },
    methods: {
        initialize() {
            if (!this.hasRegistrationTemplates) {
                this.updateLoading(false)
                return
            }
            this.updateLoading(true)
            this.refetch()
        },
        addRegistrationTemplate() {
            router.push({ name: 'event-registration-template-main', params: { id: 'new' } })
        },
        handleClick(item) {
            router.push({ name: 'event-registration-template-main', params: { id: item.id } })
        },
        handleSizeChange(itemsPerPage) {
            this.updateLoading(true)
            this.registrationTemplateStore.defineItemsPerPage(itemsPerPage)
            this.refetch()
        },
        handleCurrentChange (currentPage) {
            this.updateLoading(true)
            this.registrationTemplateStore.defineCurrentPage(currentPage)
            this.refetch()
        },
        handleColsShow(visibleColumns) {
            this.registrationTemplateStore.defineActiveColumns(visibleColumns)
            apolloClient.clearStore()
        },
        handleFilterInputChange(key, value, debounce = true) {
            this.registrationTemplateStore.defineFilterValue({ key, value })
            if (debounce) this.debouncedRefetch()
            else this.refetch()
        },
        handleSortInputChange(key, value) {
            this.registrationTemplateStore.defineSorting({ key, value })
            this.refetch()
        },
        toggleFilterVisiblity() {
            const value = ! this.showFilters

            this.disable.showFilters = true
            this.registrationTemplateStore.defineFilterVisibility(value)
            this.setupStore.saveFilterVisibility({ scope: 'ex', key: 'evnt_registration_template', value })
                .finally(() => this.disable.showFilters = false)
        },
        refetch() {
            // always add the cms id to filters
            this.registrationTemplateStore.defineFilterValue({
                key: 'cms',
                value: { key: 'cms', match: this.activeCms.id },
            })

            this.registrationTemplateStore.fetchRegistrationTemplatesPaginated()
                .then(() => this.updateLoading(false))
                .catch(err => {
                    const { graphQLErrors } = err

                    let code = 501;
                    let message = 'An error occurred!';

                    if (graphQLErrors) {
                        code = _.get(graphQLErrors, '0.code') || code;
                        message = _.get(graphQLErrors, '0.message') || message;
                    }

                    let notify = true;
                    switch (code) {
                        case 401:
                            notify = false;
                            this.error = { code, message: 'You are not authorized' };
                            break;
                        case 403:
                            notify = false;
                            this.error = { code, message: 'You are not allowed to be here' };
                            break;
                        default:
                            this.error = true;
                            break;
                    }

                    if (notify) {
                        this.$notify.error({
                            title: this.$t(message),
                            message: this.$t('Registration form list could not be fetched.'),
                            iconClass: 'fas el-icon-fa-times',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000
                        });
                    }

                    this.updateLoading(false);

                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err);
                })
        },
        debouncedRefetch: _.debounce(function() { // function scope is needed here
            this.updateLoading(true)
            this.refetch()
        }, useSetupStore(pinia).debounce || 300),
        updateLoading(value) {
            this.registrationTemplateStore.updateLoading(value);
        },
    },
    mounted() {
        this.initialize()
    },
}
</script>

<style lang="sass" scoped>
    .error
        padding: 20px
</style>
